import Card from "@material-ui/core/Card";
import Collapse from "@material-ui/core/Collapse";
import CachedIcon from "@material-ui/icons/Cached";
import { makeStyles } from "@material-ui/styles";
import { RefObject, useEffect, useRef, useState } from "react";
import {
    changeAppState,
    getAppState,
    useAppState,
} from "../../../../../common/appState";
import { sleepAsync } from "../../../../../common/functions";
import { useAbTest } from "../../../../../common/hooks/useAbTest";
import { useDelayedState } from "../../../../../common/hooks/useDelayedState";
import { useUser } from "../../../../../common/hooks/useUser";
import { fetchPost, fetchPostFormData } from "../../../../../common/util/fetch";
import { FetchResult } from "../../../../../types/fetch";
import { RightPanel } from "../../../../shared/Panel/RightPanel";
import { theme } from "../../../../zApps/Layout";
import ShurikenProgress from "../../../Animations/ShurikenProgress";
import { shurikenProgressKey } from "../../../Markdown/ImageRender/SocialPostImage";
import { User, UserForRanking } from "../../../User/types";
import { PostCard } from "./components/PostCard";
import {
    InputFieldArea,
    SocialInputReturn,
} from "./components/PostCard/RightContents/CommentArea/InputFieldArea";
import { UnreadCommentNotificationList } from "./components/UnreadCommentNotificationList";
import { useSyncPostsStateWithBackgroundPanel } from "./hooks/useSyncPostsStateWithBackgroundPanel";
import {
    FetchPostsResponse,
    OriginKey,
    Post,
    PostToShow,
    UnreadComment,
} from "./types";

const transitionMilliseconds = 1000;
export const panelWidth = 500;

const SocialPanelOriginKey: OriginKey = "SocialPanel";
export default function SocialPanel() {
    const [panelState, setPanelState] = useAppState("socialPanelState");

    const { delayedOpen } = useDelayedState(
        panelState,
        ({ open }) => open,
        transitionMilliseconds
    );

    const scrollableContainerRef = useRef<HTMLDivElement>(null);
    const c = usePanelStyles();

    const [posts, setPosts] = useState<Post[]>([]);

    const [minTime, setMinTime] = useState("");
    const [postedUsers, setPostedUsers] = useState<UserForRanking[]>([]);
    const { user, isUserFetchDone } = useUser();
    const userId = user?.userId ?? 0;
    const [fixedUnreadComments, setFixedUnreadComments] = useState<
        UnreadComment[]
    >([]);

    return (
        <RightPanel
            panelName="SocialPanel"
            open={panelState.open}
            onClose={() => {
                setPanelState({ open: false });
            }}
            panelWidth={panelWidth}
            globalPanelState={panelState}
            transitionMilliseconds={transitionMilliseconds}
            style={{ backgroundColor: theme.palette.grey[100] }}
            topItem={
                <CachedIcon
                    className={c.loadIcon}
                    onClick={async () => {
                        setPosts([]);
                        setMinTime("");
                        await sleepAsync(300); // Show loading animation once
                        initialFetch(
                            userId,
                            setPosts,
                            setMinTime,
                            setPostedUsers,
                            0
                        );
                    }}
                />
            }
            childrenContainerStyle={{
                display: "flex",
                flexDirection: "column",
            }}
        >
            {fixedUnreadComments.length > 0 && (
                <UnreadCommentNotificationList
                    fixedUnreadComments={fixedUnreadComments}
                    scrollableContainerRef={scrollableContainerRef}
                    loading={minTime === ""}
                    originKey={SocialPanelOriginKey}
                />
            )}

            <div className={c.contentWrapper} ref={scrollableContainerRef}>
                {delayedOpen && (
                    <SocialContent
                        originKey={SocialPanelOriginKey}
                        scrollableContainerRef={scrollableContainerRef}
                        posts={posts}
                        setPosts={setPosts}
                        minTime={minTime}
                        setMinTime={setMinTime}
                        postedUsers={postedUsers}
                        setPostedUsers={setPostedUsers}
                        user={user}
                        userId={userId}
                        isUserFetchDone={isUserFetchDone}
                        scrollableSpanToFetch={1000}
                        fixedUnreadComments={fixedUnreadComments}
                        setFixedUnreadComments={setFixedUnreadComments}
                    />
                )}
            </div>
        </RightPanel>
    );
}
const usePanelStyles = makeStyles({
    loadIcon: {
        position: "absolute",
        top: 7,
        right: 7,
        maxHeight: 27,
        minHeight: 27,
        maxWidth: 27,
        minWidth: 27,
        cursor: "pointer",
    },
    contentWrapper: { height: "100%", overflow: "auto" },
});

type SavedInitialData = { posts: Post[]; postedUsers: UserForRanking[] };
async function initialFetch(
    userId: number,
    setPosts: (posts: Post[]) => void,
    setMinTime: (minTime: string) => void,
    setPostedUsers: (postedUsers: UserForRanking[]) => void,
    targetUserId: number
) {
    const saveKey = getLocalStorageSaveKey(targetUserId);
    const savedData = localStorage.getItem(saveKey);

    setMinTime("");
    if (savedData) {
        const previousResult: SavedInitialData = JSON.parse(savedData);
        setPosts(previousResult.posts);
        setPostedUsers(previousResult.postedUsers);
    } else {
        setPosts([]);
        setPostedUsers([]);
    }

    const { unreadComments } = getAppState();
    const result = await fetchPosts("", userId, unreadComments, targetUserId);
    if (!result || "error" in result) {
        return;
    }
    setPosts(result.posts);
    setMinTime(result.minTime);
    setPostedUsers(result.postedUsers);

    sleepAsync(1000).then(() => {
        saveDataToLocalStorage(result.posts, result.postedUsers, saveKey);
    });
}
function getLocalStorageSaveKey(targetUserId: number) {
    return `social-posts-initial-fetch-${targetUserId}`;
}
function saveDataToLocalStorage(
    posts: Post[],
    postedUsers: UserForRanking[],
    saveKey: string
) {
    try {
        if (posts.length <= 30) {
            const dataToSave: SavedInitialData = {
                posts,
                postedUsers,
            };
            localStorage.setItem(saveKey, JSON.stringify(dataToSave));
            return;
        }

        // No need to save more than 30 posts because cost to render should be reduced
        const shortenPosts = posts.slice(0, 30);
        const shortenPostedUserIds = shortenPosts.map(p => p.userId);

        const shortenDataToSave: SavedInitialData = {
            posts: shortenPosts,
            postedUsers: postedUsers.filter(u =>
                shortenPostedUserIds.includes(u.userId)
            ),
        };
        localStorage.setItem(saveKey, JSON.stringify(shortenDataToSave));
    } catch (ex: any) {
        if (ex.toString().includes("QuotaExceededError")) {
            localStorage.clear();
            location.reload();
        }
    }
}

export const finishedKey = "finished";
export function SocialContent({
    scrollableContainerRef,
    posts,
    setPosts,
    minTime,
    setMinTime,
    postedUsers,
    setPostedUsers,
    user,
    userId,
    isUserFetchDone,
    isPostInputHidden,
    targetUserId = 0,
    scrollableSpanToFetch,
    hideLevelAndCountry,
    hideButtonTitle,
    initialShurikenSize = 60,
    fixedUnreadComments,
    setFixedUnreadComments,
    shouldNotReadComments,
    originKey,
    checkFetchNecessary,
    socialCardRef,
}: {
    scrollableContainerRef: RefObject<HTMLDivElement>;
    posts: Post[];
    setPosts: (posts: Post[]) => void;
    minTime: string;
    setMinTime: (minTime: string) => void;
    postedUsers: UserForRanking[];
    setPostedUsers: (postedUsers: UserForRanking[]) => void;
    user?: User;
    userId: number;
    isUserFetchDone: boolean;
    isPostInputHidden?: boolean;
    targetUserId?: number;
    scrollableSpanToFetch: number;
    hideLevelAndCountry?: boolean;
    hideButtonTitle?: boolean;
    initialShurikenSize?: number;
    fixedUnreadComments: UnreadComment[];
    setFixedUnreadComments: (unreadComments: UnreadComment[]) => void;
    shouldNotReadComments?: boolean;
    originKey: OriginKey;
    checkFetchNecessary?: () => boolean;
    socialCardRef?: RefObject<HTMLDivElement>;
}) {
    useSyncPostsStateWithBackgroundPanel(originKey, posts, setPosts); // Sync data with other panel

    const dataToSave = { posts, postedUsers, targetUserId };
    const ref = useRef(dataToSave); // インスタンスを変えないためにrefを使う
    ref.current = dataToSave;
    useEffect(() => {
        // Clean up when close panel
        return () => {
            setPosts([]);
            setMinTime("");
            setPostedUsers([]);
            setFixedUnreadComments([]);

            // Save data to LocalStorage
            const { posts, postedUsers, targetUserId } = ref.current;
            saveDataToLocalStorage(
                posts,
                postedUsers,
                getLocalStorageSaveKey(targetUserId)
            );
        };
    }, [setPosts, setMinTime, setPostedUsers, setFixedUnreadComments]);

    const isScrollFinished = minTime === finishedKey;
    const initialLoading = minTime === "";
    const [loadingByScroll, setLoadingByScroll] = useState(false);

    const [tmpMutedUserIds, setTmpMutedUserIds] = useState<number[]>([]);

    useEffect(() => {
        if (!isUserFetchDone) {
            return;
        }
        initialFetch(
            userId,
            setPosts,
            setMinTime,
            setPostedUsers,
            targetUserId
        );
    }, [
        userId,
        setPosts,
        setMinTime,
        setPostedUsers,
        isUserFetchDone,
        targetUserId,
    ]);

    useEffect(() => {
        if (shouldNotReadComments) {
            return;
        }
        const { unreadComments } = getAppState();
        setFixedUnreadComments(unreadComments);

        if (unreadComments.length > 0) {
            changeAppState("unreadComments", []);
            fetch(`api/PostComment/ReadComments?UserId=${userId}`);
        }
    }, [userId, shouldNotReadComments]);

    useEffect(() => {
        const el = scrollableContainerRef?.current;
        if (!el) {
            return;
        }
        const onScroll = async () => {
            if (isScrollFinished) {
                return; // Already fetched all data
            }
            if (
                el.scrollHeight >
                el.offsetHeight + el.scrollTop + scrollableSpanToFetch
            ) {
                // Still scrollable more than scrollableSpanToFetch px
                if (!checkFetchNecessary?.()) {
                    // Also, there's no special demand to fetch
                    return;
                }
            }
            if (minTime === "") {
                return; // Scrolled before finishing the initial fetch
            }
            setLoadingByScroll(true);
            const result = await fetchPosts(minTime, userId, [], targetUserId);
            if (result == null) {
                return; // duplicated fetch
            }
            if ("error" in result) {
                setLoadingByScroll(false);
                return;
            }
            setPosts(posts.concat(result.posts));
            setMinTime(result.minTime);
            setPostedUsers(postedUsers.concat(result.postedUsers));
            setLoadingByScroll(false);
        };
        el.addEventListener("scroll", onScroll);
        return () => {
            el.removeEventListener("scroll", onScroll);
        };
    }, [
        scrollableContainerRef,
        minTime,
        posts,
        postedUsers,
        isScrollFinished,
        targetUserId,
        scrollableSpanToFetch,
        checkFetchNecessary,
    ]);

    const c = useContentStyles();

    const hasSubmittingPost = posts.some(p => p.postNo < 1);
    const isShowingLocallySavedPosts = minTime === "" && posts.length > 0;

    const [bottomOfVisibleArea, setBottomOfVisibleArea] =
        useState(screenExpectedHeight);
    useEffect(() => {
        const scrollableEl = scrollableContainerRef.current;
        if (!scrollableEl) {
            return;
        }
        const onScroll = () => {
            setBottomOfVisibleArea(
                scrollableEl.scrollTop + screenExpectedHeight
            );
        };
        scrollableEl.addEventListener("scroll", onScroll);

        return () => {
            scrollableEl.removeEventListener("scroll", onScroll);
        };
    }, [scrollableContainerRef]);

    return (
        <div className={c.container} ref={socialCardRef}>
            {!isPostInputHidden && (
                <Card className={c.inputCard}>
                    <PostInputField
                        posts={posts}
                        setPosts={setPosts}
                        postedUsers={postedUsers}
                        setPostedUsers={setPostedUsers}
                        user={user}
                        userId={userId}
                        hasSubmittingPost={hasSubmittingPost}
                    />
                </Card>
            )}

            <Collapse in={isShowingLocallySavedPosts} timeout={500}>
                <ShurikenProgress
                    size={initialShurikenSize}
                    className={c.unloadedShuriken}
                />
            </Collapse>

            {posts.map((post, index) => {
                const unreadCommentNos = fixedUnreadComments
                    .filter(
                        comment =>
                            comment.postedUserId === post.userId &&
                            comment.postNo === post.postNo
                    )
                    .map(comment => comment.commentNo);

                if (unreadCommentNos.length < 1) {
                    const topOfThisCard =
                        (index + 1) * minimumPostCardHeight +
                        inputFieldMinimumHeight;
                    if (bottomOfVisibleArea < topOfThisCard) {
                        /**
                         * Virtual scroll
                         */
                        return (
                            <div
                                key={`${post.userId}-${post.postNo}`}
                                className={c.dummyDivForVirtualScroll}
                            />
                        );
                    }
                }

                return (
                    <PostCard
                        key={`${post.userId}-${post.postNo}`}
                        post={post}
                        postedUsers={postedUsers}
                        posts={posts}
                        setPosts={setPosts}
                        userId={userId}
                        tmpMutedUserIds={tmpMutedUserIds}
                        setTmpMutedUserIds={setTmpMutedUserIds}
                        user={user}
                        unreadCommentNos={unreadCommentNos}
                        hideLevelAndCountry={hideLevelAndCountry}
                        hideButtonTitle={hideButtonTitle}
                        hideTime={isShowingLocallySavedPosts}
                        scrollableContainerRef={scrollableContainerRef}
                        originKey={originKey}
                    />
                );
            })}

            {(initialLoading || loadingByScroll) && (
                <ShurikenProgress size={30} className={c.loadingShuriken} />
            )}
        </div>
    );
}
const screenExpectedHeight = 2000;
const minimumPostCardHeight = 130;
const inputFieldMinimumHeight = 71;
const useContentStyles = makeStyles({
    unloadedShuriken: { margin: "50px 0 40px" },
    loadingShuriken: { margin: "10px 0" },
    container: {
        paddingTop: 1,
        textAlign: "left",
    },
    inputCard: { padding: "10px 5px", margin: 5 },
    dummyDivForVirtualScroll: { height: minimumPostCardHeight },
});

function PostInputField({
    posts,
    setPosts,
    postedUsers,
    setPostedUsers,
    user,
    userId,
    hasSubmittingPost,
}: {
    posts: Post[];
    setPosts: (posts: Post[]) => void;
    postedUsers: UserForRanking[];
    setPostedUsers: (postedUsers: UserForRanking[]) => void;
    user?: User;
    userId: number;
    hasSubmittingPost: boolean;
}) {
    const { abTestKey, abTestSuccess } = useAbTest({
        testName: "SocialPostInput-placeholder",
        keys: Object.keys(placeholders),
    });
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <InputFieldArea
            inputRef={inputRef}
            user={user}
            sendText={async (text, files): Promise<SocialInputReturn> => {
                if (!user) {
                    changeAppState("signInPanelState", {
                        type: "signUp",
                    });
                    return "shouldNotClearField";
                }
                if (hasSubmittingPost) {
                    return "shouldNotClearField";
                }

                if (!postedUsers.some(u => u.userId === user.userId)) {
                    setPostedUsers([...postedUsers, user]);
                }
                const hasFile = files.length > 0;
                const newPosts = [getTmpPost(userId, text, hasFile), ...posts];
                setPosts(newPosts);
                const newPost = hasFile
                    ? await registerImagePost(userId, text, files)
                    : await registerPost(userId, text);

                if (!newPost || "error" in newPost) {
                    alert("Failed to post");
                    return "shouldNotClearField";
                }
                setPosts(newPosts.map(p => (p.postNo < 1 ? newPost : p)));
                abTestSuccess();
            }}
            placeholder={abTestKey && placeholders[Number(abTestKey)]}
            includeQuestion
            disabled={hasSubmittingPost}
            autoFocus
        />
    );
}
const placeholders = [
    "What did you learn from this website today?",
    "What is your favorite way to study Japanese?",
    "What are you planning to study tomorrow?",
    "What do you want to do in Japan?",
    "What will you do after mastering the Japanese language?",
    "Do you enjoy this website?",
    "Is this website helpful to you?",
    "What do you find most difficult when studying Japanese?",
    "Is the Japanese language difficult for you?",
    "What is your favorite Japanese food?",
    "How's your studying going?",
    "How are you today?",
    "Why are you learning Japanese?",
    "Post your favorite picture of Japan!",
    "Post anything you like!",
    "What is your favorite anime?",
    "すきな アニメは なんですか？",
    "すきな マンガは なんですか？",
    "げんき ですか？",
    "にほんごは たのしい ですか？",
    "すきな にほんごは なんですか？",
    "すきな にほんの たべものは なんですか？",
    "Do you have questions about the Japanese language?",
    "You can ask questions about Japanese here!",
    "Do you have any questions?",
    "Do you have any questions about Japan?",
];
function getTmpPost(userId: number, text: string, hasFile: boolean): Post {
    return {
        userId,
        postNo: 0,
        time: "",
        minutesAgo: 0,
        text: hasFile ? `${text}![shuriken](${shurikenProgressKey})` : text,
        isAutoPost: false,
        deleted: false,
        likeCount: 0,
        commentCount: 0,
        ownLike: false,
        imageExtensions: "",
    };
}

let lastParam = "";
async function fetchPosts(
    minTime: string,
    userId: number,
    postsToShow: PostToShow[],
    targetUserId: number
): Promise<FetchPostsResponse | null> {
    if (minTime !== "" && lastParam === minTime) {
        return null;
    }
    lastParam = minTime;

    return await fetchPost<
        {
            minTime: string;
            userId: number;
            postsToShow: PostToShow[];
            targetUserId: number;
        },
        FetchPostsResponse
    >("api/Post/GetPosts", { minTime, userId, postsToShow, targetUserId });
}

async function registerPost(
    userId: number,
    text: string
): Promise<FetchResult<Post | null>> {
    return await fetchPost<
        {
            userId: number;
            text: string;
        },
        FetchResult<Post | null>
    >("api/Post/RegisterPost", { userId, text });
}

async function registerImagePost(
    userId: number,
    text: string,
    files: File[]
): Promise<FetchResult<Post | null>> {
    const formData = new FormData();
    formData.append("userId", userId.toString());
    formData.append("text", text);
    for (let file of files) {
        formData.append("files", file);
    }

    return await fetchPostFormData<FetchResult<Post | null>>(
        "api/Post/PostImages",
        formData
    );
}
